import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, FileText, Cookie } from 'lucide-react';

const LegalPage = ({ type }) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [type]);

    const content = {
        'privacy-policy': {
            title: 'Privacy Policy',
            icon: Shield,
            lastUpdated: 'October 28, 2026',
            intro: (
                <>
                    At <strong className="text-dark font-bold">MiniApp Tech</strong>, accessible from{' '}
                    <a href="https://miniapptech.in/" className="text-dark font-bold hover:text-primary underline" target="_blank" rel="noopener noreferrer">
                        https://miniapptech.in/
                    </a>
                    , we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data.
                </>
            ),
            sections: [
                {
                    heading: '1. Information We Collect',
                    text: (
                        <div className="space-y-4">
                            <p>We may collect the following information when you visit or interact with our website:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Full Name</li>
                                <li>Phone Number</li>
                                <li>Email Address</li>
                                <li>WhatsApp Number</li>
                                <li>Company Name (if applicable)</li>
                                <li>Any information submitted through enquiry, contact, or service forms</li>
                            </ul>
                            <p>We may also automatically collect technical information such as IP address, browser type, and pages visited.</p>
                        </div>
                    )
                },
                {
                    heading: '2. How We Use Your Information',
                    text: (
                        <div className="space-y-4">
                            <p>Your information may be used to:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Respond to your enquiries and service requests</li>
                                <li>Provide information about our services</li>
                                <li>Improve our website and service offerings</li>
                                <li>Communicate important updates or offers</li>
                            </ul>
                        </div>
                    )
                },
                {
                    heading: '3. Communication Consent',
                    text: (
                        <div className="space-y-4">
                            <p>
                                By submitting your information on{' '}
                                <a href="https://miniapptech.in/" className="text-dark font-bold hover:text-primary underline" target="_blank" rel="noopener noreferrer">
                                    https://miniapptech.in/
                                </a>
                                , you <strong className="text-dark font-bold">authorize MiniApp Tech to contact you via phone calls, SMS, RCS messages, WhatsApp, or email</strong> for <strong className="text-dark font-bold">informational and promotional purposes related to our services</strong>.
                            </p>
                            <p>
                                You may <strong className="text-dark font-bold">opt out of promotional communications at any time</strong> by contacting us using the details provided below.
                            </p>
                        </div>
                    )
                },
                {
                    heading: '4. Cookies',
                    text: 'We use cookies and similar tracking technologies to enhance user experience and analyze website traffic. You can disable cookies through your browser settings.'
                },
                {
                    heading: '5. Data Security',
                    text: 'We implement reasonable security measures to protect your personal data. However, no internet-based system is completely secure, and we cannot guarantee absolute security.'
                },
                {
                    heading: '6. Third-Party Links',
                    text: 'Our website may contain links to third-party websites. MiniApp Tech is not responsible for the privacy practices or content of such websites.'
                },
                {
                    heading: '7. Your Consent',
                    text: 'By using our website and submitting your information, you consent to this Privacy Policy.'
                },
                {
                    heading: '8. Contact Information',
                    text: (
                        <div className="space-y-3">
                            <p>
                                For privacy-related concerns or to opt out of promotional communication, please contact us:
                            </p>
                            <div className="space-y-2 pt-1">
                                <p className="flex items-center gap-2">
                                    
                                    <strong className="text-dark font-bold">Email:</strong>{' '}
                                    <a href="mailto:hr@miniapptech.in" className="text-primary hover:underline">
                                        hr@miniapptech.in
                                    </a>
                                </p>
                                <p className="flex items-center gap-2">
                                   
                                    <strong className="text-dark font-bold">Website:</strong>{' '}
                                    <a href="https://miniapptech.in/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                                        https://miniapptech.in/
                                    </a>
                                </p>
                            </div>
                        </div>
                    )
                }
            ]
        },
        'terms-of-service': {
            title: 'Terms of Service',
            icon: FileText,
            lastUpdated: 'October 28, 2026',
            intro: (
                <>
                    Welcome to <strong className="text-dark font-bold">MiniApp Tech</strong>. By accessing and using{' '}
                    <a href="https://miniapptech.in/" className="text-dark font-bold hover:text-primary underline" target="_blank" rel="noopener noreferrer">
                        https://miniapptech.in/
                    </a>
                    , you agree to comply with and be bound by the following Terms &amp; Conditions.
                </>
            ),
            sections: [
                {
                    heading: '1. Acceptance of Terms',
                    text: 'By accessing this website or submitting your information, you agree to these Terms & Conditions in full.'
                },
                {
                    heading: '2. Use of Website',
                    text: (
                        <ul className="list-disc pl-6 space-y-2">
                            <li>The content on this website is for general information purposes only</li>
                            <li>You agree not to misuse the website or attempt unauthorized access</li>
                            <li>Any misuse may result in legal action</li>
                        </ul>
                    )
                },
                {
                    heading: '3. User Information & Communication Consent',
                    text: (
                        <div className="space-y-4">
                            <p>
                                When you submit your personal details through forms on this website, you expressly{' '}
                                <strong className="text-dark font-bold">
                                    authorize MiniApp Tech to contact you via phone, SMS, RCS, WhatsApp, or email
                                </strong>{' '}
                                for{' '}
                                <strong className="text-dark font-bold">
                                    informational and promotional purposes related to our services
                                </strong>.
                            </p>
                            <p>
                                You can{' '}
                                <strong className="text-dark font-bold">
                                    opt out of promotional communication at any time
                                </strong>{' '}
                                by contacting us directly.
                            </p>
                        </div>
                    )
                },
                {
                    heading: '4. Accuracy of Information',
                    text: 'You agree that the information provided by you is accurate and complete to the best of your knowledge.'
                },
                {
                    heading: '5. Intellectual Property Rights',
                    text: (
                        <>
                            All content, including text, graphics, logos, and design elements on this website, are the property of{' '}
                            <strong className="text-dark font-bold">MiniApp Tech</strong> and may not be copied, reproduced, or distributed without written permission.
                        </>
                    )
                },
                {
                    heading: '6. Limitation of Liability',
                    text: 'MiniApp Tech shall not be liable for any direct or indirect damages arising from the use of this website or our services.'
                },
                {
                    heading: '7. External Links',
                    text: 'Our website may include links to external websites. We are not responsible for the content or policies of third-party sites.'
                },
                {
                    heading: '8. Modifications to Terms',
                    text: 'We reserve the right to update or modify these Terms & Conditions at any time without prior notice. Continued use of the website constitutes acceptance of revised terms.'
                },
                {
                    heading: '9. Governing Law',
                    text: (
                        <>
                            These Terms &amp; Conditions shall be governed and interpreted in accordance with the laws of{' '}
                            <strong className="text-dark font-bold">India</strong>.
                        </>
                    )
                },
                {
                    heading: '10. Contact Details',
                    text: (
                        <div className="space-y-3">
                            <p>
                                For any queries regarding these Terms &amp; Conditions or communication preferences, please contact:
                            </p>
                            <div className="space-y-2 pt-1">
                                <p className="flex items-center gap-2">
                                  
                                    <strong className="text-dark font-bold">Email:</strong>{' '}
                                    <a href="mailto:hr@miniapptech.in" className="text-primary hover:underline">
                                        hr@miniapptech.in
                                    </a>
                                </p>
                                <p className="flex items-center gap-2">
                                    <strong className="text-dark font-bold">Website:</strong>{' '}
                                    <a href="https://miniapptech.in/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                                        https://miniapptech.in/
                                    </a>
                                </p>
                            </div>
                        </div>
                    )
                }
            ]
        },
        'cookie-policy': {
            title: 'Cookie Policy',
            icon: Cookie,
            lastUpdated: 'February 23, 2026',
            sections: [
                {
                    heading: '1. What Are Cookies',
                    text: 'Cookies are small text files that are stored on your computer or mobile device when you visit a website. They are widely used to make websites work or work more efficiently.'
                },
                {
                    heading: '2. How We Use Cookies',
                    text: 'We use cookies to understand how you use our website and to improve your experience. This includes keeping you logged in and remembering your preferences.'
                },
                {
                    heading: '3. Managing Cookies',
                    text: 'Most web browsers allow you to control cookies through their settings preferences. However, if you limit the ability of websites to set cookies, you may worsen your overall user experience.'
                }
            ]
        }
    };

    const currentContent = content[type] || content['privacy-policy'];
    const Icon = currentContent.icon;

    return (
        <div className="pt-24 min-h-screen bg-white">
            <section className="py-20 bg-dark text-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center"
                    >
                        <div className="inline-block p-4 bg-primary/20 rounded-2xl mb-6">
                            <Icon size={40} className="text-primary" />
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-4">{currentContent.title}</h1>
                        <p className="text-gray-400">Last Updated: {currentContent.lastUpdated}</p>
                    </motion.div>
                </div>
            </section>

            <section className="py-20">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="space-y-12"
                    >
                        {currentContent.intro && (
                            <p className="text-gray-700 leading-relaxed text-lg pb-6 border-b border-gray-100">
                                {currentContent.intro}
                            </p>
                        )}
                        {currentContent.sections.map((section, idx) => (
                            <div key={idx}>
                                <h2 className="text-2xl font-bold text-dark mb-4">{section.heading}</h2>
                                <div className="text-gray-600 leading-relaxed text-lg">{section.text}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default LegalPage;
